import type { PluginDefinition } from "@magic-md/core";

export interface WorkspacePinnedUnitView {
    mode: "docked" | "floating";
    x?: number;
    y?: number;
    width?: number;
    height?: number;
}

export interface WorkspacePinnedUnit {
    id: string;
    documentPath: string;
    unitId: string;
    unitType: "definition";
    pluginName: string;
    label?: string;
    addedAt: number;
    view?: WorkspacePinnedUnitView;
}

function hashPinnedUnitIdentity(documentPath: string, unitId: string): string {
    const source = `${documentPath}\0${unitId}`;
    let hash = 5381;
    for (let i = 0; i < source.length; i += 1) {
        hash = ((hash << 5) + hash) ^ source.charCodeAt(i);
    }
    return (hash >>> 0).toString(36);
}

export function createWorkspacePinnedUnitId(
    documentPath: string,
    unitId: string,
): string {
    return `unit:${hashPinnedUnitIdentity(documentPath, unitId)}`;
}

export function isSamePinnedUnitTarget(
    unit: Pick<WorkspacePinnedUnit, "documentPath" | "unitId">,
    documentPath: string | null | undefined,
    unitId: string,
): boolean {
    return !!documentPath &&
        unit.documentPath === documentPath &&
        unit.unitId === unitId;
}

export function createWorkspacePinnedUnit(
    documentPath: string,
    definition: Pick<PluginDefinition, "id" | "pluginName">,
): WorkspacePinnedUnit {
    return {
        id: createWorkspacePinnedUnitId(documentPath, definition.id),
        documentPath,
        unitId: definition.id,
        unitType: "definition",
        pluginName: definition.pluginName,
        label: definition.id,
        addedAt: Date.now(),
        view: { mode: "docked" },
    };
}

export function findWorkspacePinnedUnit(
    units: readonly WorkspacePinnedUnit[],
    documentPath: string | null | undefined,
    unitId: string,
): WorkspacePinnedUnit | undefined {
    return units.find((unit) =>
        isSamePinnedUnitTarget(unit, documentPath, unitId),
    );
}

export function collectPinnedUnitIdsForDocument(
    units: readonly WorkspacePinnedUnit[],
    documentPath: string | null | undefined,
): Set<string> {
    const result = new Set<string>();
    if (!documentPath) return result;
    for (const unit of units) {
        if (unit.documentPath === documentPath) result.add(unit.unitId);
    }
    return result;
}
